import { useState, useMemo, useRef } from "react";
import { useFormikContext } from "formik";
import Card from "../Card";
import SectionHeader from "../SectionHeader";
import { PreconFormValues } from "../../types/precon.types";
import DrawRequestIcon from "Assets/Img/DrawRequestIcon";
import { previewPreconPdf } from "hooks/usePreconPdf";
import ChipEmailInput from "../../../../../Components/Common/ChipEmailInput/ChipEmailInput";
import Input from "Components/Common/Input/Input";
import Table, { Column } from "Components/Table/Table";
import Select from "Components/Common/Select/Select";
import Button from "Components/Common/Button/Button";

interface Props {
  onToast: (msg: string) => void;
  onSaveDraft: () => void;
  onCcErrorChange?: (hasError: boolean, scrollFn: () => void) => void;
}

const BULLET_RE = /^[-•*·]\s+/;

const DELIVERY_METHOD_OPTIONS = [
  { value: "email_platform", label: "Email + In-platform notification" },
  { value: "platform", label: "In-platform only" },
  { value: "email", label: "Email only" },
];

const LOCK_OPTIONS = [
  { value: "yes", label: "Yes — lock for editing after publish" },
  { value: "no", label: "No — allow revisions" },
];

const renderBulletText = (text: string) => {
  const lines = text.split("\n").filter((l) => l.trim());
  return (
    <ul>
      {lines.map((line, i) => (
        <li key={i}>{line.replace(BULLET_RE, "")}</li>
      ))}
    </ul>
  );
};

const statusStyle = (status: string) => ({
  color:
    status === "Complete"
      ? "var(--green)"
      : status === "N/A"
        ? "var(--tx2)"
        : "var(--amber)",
});

const splitLines = (text: string): string[] =>
  text
    ? text
        .split("\n")
        .filter((l) => l.trim())
        .map((l) => l.replace(BULLET_RE, "").trim())
    : [];

const parseNum = (val: string | undefined) =>
  parseFloat((val ?? "").replace(/[$,]/g, "")) || 0;

const Step8PreviewPublish = ({
  onToast,
  onSaveDraft,
  onCcErrorChange,
}: Props) => {
  const { values, errors, touched, handleChange, handleBlur, setFieldValue } =
    useFormikContext<PreconFormValues>();

  const hasErr = (field: keyof PreconFormValues): boolean =>
    !!(touched[field] && errors[field]);
  const [isPreviewing, setIsPreviewing] = useState(false);

  const ccHasError = useRef(false);
  const ccFieldRef = useRef<HTMLDivElement>(null);

  const scrollToCcError = () =>
    ccFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });

  const handleCcErrorChange = (hasError: boolean) => {
    ccHasError.current = hasError;
    onCcErrorChange?.(hasError, scrollToCcError);
  };

  const guardedSaveDraft = () => {
    if (ccHasError.current) {
      scrollToCcError();
      return;
    }
    onSaveDraft();
  };

  const handlePreview = async () => {
    setIsPreviewing(true);
    try {
      await previewPreconPdf(values);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      onToast(`PDF preview failed — downloading instead. (${msg})`);
    } finally {
      setIsPreviewing(false);
    }
  };

  const adminUser = JSON.parse(localStorage.getItem("user") || "{}") as {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string | null;
    companyName?: string;
  };
  const adminName =
    [adminUser.firstName, adminUser.lastName].filter(Boolean).join(" ") || "—";
  const adminCompany = adminUser.companyName || "";
  const adminContact =
    [adminUser.phone, adminUser.email].filter(Boolean).join(" · ") || "—";

  const totals = useMemo(() => {
    const dataRows = values.budgetRows.filter((r) => !r.isTotal);
    return {
      value: dataRows.reduce((sum, r) => sum + parseNum(r.value), 0),
      projectSf: dataRows.reduce((sum, r) => sum + parseNum(r.projectSf), 0),
      projectPsf: dataRows.reduce((sum, r) => sum + parseNum(r.projectPsf), 0),
      industryLow: dataRows.reduce(
        (sum, r) => sum + parseNum(r.industryLow),
        0,
      ),
      industryHigh: dataRows.reduce(
        (sum, r) => sum + parseNum(r.industryHigh),
        0,
      ),
    };
  }, [values.budgetRows]);

  const getYesNo = (id: string) =>
    values.yesNoItems.find((y) => y.id === id)?.value ?? "no";

  const yesLabel = (id: string, yesText: string, noText: string) =>
    getYesNo(id) === "yes" ? yesText : noText;
  return (
    <div className="screen active">
      <SectionHeader
        title="Preview & Publish"
        subtitle={`Review the compiled pre-construction report before publishing to ${values.bank || "the bank"}.`}
      />

      <div className="pdfActions">
        <Button
          className="btn btnSecondary"
          type="button"
          onClick={handlePreview}
          disabled={isPreviewing}
        >
          <DrawRequestIcon />
          {isPreviewing ? "Generating…" : "Preview as PDF"}
        </Button>
        <Button
          className="btn btnSecondary"
          type="button"
          onClick={guardedSaveDraft}
        >
          Save Draft
        </Button>
      </div>

      <div className="previewWrap">
        <div className="previewHeader">
          <div className="phLabel">Pre-Construction Review</div>
          <div className="phTitle">
            {values.projectName || "—"}
            {values.projectAddress ? ` — ${values.projectAddress}` : ""}
          </div>
          <div className="phSub">
            {values.reportDate} &nbsp;·&nbsp; {values.bank || "—"} &nbsp;·&nbsp;{" "}
            {values.lendingOfficer || "—"} &nbsp;·&nbsp; Invoice{" "}
            {values.invoiceNumber
              ? values.invoiceNumber.startsWith("#")
                ? values.invoiceNumber
                : `#${values.invoiceNumber}`
              : "#—"}
          </div>
        </div>

        <div className="previewBody">
          {/* ── Invoice & Project Overview ── */}
          <div className="ps">
            <div className="psTitle">Invoice &amp; Project Overview</div>
            <div className="psBody">
              <strong>To:</strong> {values.bank || "—"} &nbsp;·&nbsp; Attn:{" "}
              {values.lendingOfficer || "—"}
              <br />
              <strong>Project:</strong> {values.projectName || "—"}{" "}
              &nbsp;·&nbsp; {values.projectAddress || "—"}
              <br />
              <strong>For:</strong>{" "}
              {values.servicesRequested || "Preconstruction Report"}{" "}
              &nbsp;·&nbsp;{" "}
              <strong>
                Total Due: {values.invoiceAmount || "—"} &nbsp;·&nbsp; Invoice{" "}
                {values.invoiceNumber
                  ? values.invoiceNumber.startsWith("#")
                    ? values.invoiceNumber
                    : `#${values.invoiceNumber}`
                  : "#—"}
              </strong>
              <br />
              <br />
              <strong>Owner:</strong> {values.ownerName || "—"} &nbsp;·&nbsp;{" "}
              <strong>Contractor:</strong> {values.gcName || "—"} &nbsp;·&nbsp;{" "}
              <strong>Services:</strong> {values.servicesRequested || "—"}
            </div>
          </div>

          {/* ── Certification ── */}
          <div className="ps">
            <div className="psTitle">Certification</div>
            <div className="psBody" style={{ whiteSpace: "pre-wrap" }}>
              {values.certificationOpening || "—"}
            </div>
          </div>

          {/* ── Executive Summary ── */}
          <div className="ps">
            <div className="psTitle">
              Project Description / Executive Summary
            </div>
            <div className="psBody">
              <ul>
                {splitLines(values.primaryDescriptionPoints).map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
                {values.totalSf && (
                  <li>
                    Approximate total square footage:{" "}
                    <strong>{values.totalSf}</strong>
                  </li>
                )}
                {(values.totalBudget || values.hardCostBudget) && (
                  <li>
                    Total budget: <strong>{values.totalBudget || "—"}</strong>
                    {values.hardCostBudget && (
                      <>
                        {" "}
                        &nbsp;·&nbsp; Hard cost budget:{" "}
                        <strong>{values.hardCostBudget}</strong>
                        {values.costPerSf && <> (~{values.costPerSf}/SF)</>}
                      </>
                    )}
                  </li>
                )}
                {(values.mobilizationDate || values.estCompletion) && (
                  <li>
                    Mobilization: {values.mobilizationDate || "—"} &nbsp;·&nbsp;
                    Est. completion: {values.estCompletion || "—"}
                    {values.statedDuration && ` (${values.statedDuration})`}
                  </li>
                )}
                {splitLines(values.additionalDescription).map((line, i) => (
                  <li key={`ad-${i}`}>{line}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* ── Contract Delivery ── */}
          <div className="ps">
            <div className="psTitle">Contract Delivery Method &amp; Review</div>
            <div className="psBody">
              <ul>
                {values.gcName && (
                  <li>
                    {values.gcName} —{" "}
                    {values.contractType || "contract type not specified"} with
                    budget of {values.totalBudget || "—"}
                  </li>
                )}
                <li>
                  {values.contractorFee
                    ? `Contractor Fee: ${values.contractorFee}`
                    : "No Contractor Fee carried"}
                  {values.contingencyDisplay &&
                    ` · Contingency: ${values.contingencyDisplay}`}
                  {values.contingencyPct &&
                    ` (${values.contingencyPct} of hard costs)`}
                </li>
                <li>
                  {yesLabel(
                    "permits",
                    "Permits by Owner/GC — building permits requested prior to first disbursement",
                    "Permits — not confirmed",
                  )}
                </li>
                <li>
                  {yesLabel(
                    "buildersRisk",
                    "Builder's Risk and Liability/Workers Comp Insurance by Owner/GC — certificates attached",
                    "Builder's Risk Insurance — not confirmed",
                  )}
                </li>
                <li>
                  {yesLabel(
                    "bond",
                    "Performance and Payment Bond required",
                    "No Performance and Payment Bond",
                  )}
                  {" · "}
                  {yesLabel(
                    "liquidated",
                    "Liquidated damages apply",
                    "No liquidated damages",
                  )}
                  {" · "}
                  {yesLabel(
                    "bonus",
                    "Early completion bonus applies",
                    "No early completion bonus",
                  )}
                  {" · "}
                  {yesLabel(
                    "savings",
                    "Shared savings clause included",
                    "No shared savings",
                  )}
                </li>
                {values.retainage && (
                  <li>
                    {values.retainage}% retainage stated — 5–10% is typical.
                    Lender should verify retainage requirements with Lending
                    Agreement.
                  </li>
                )}
                {splitLines(values.contractNarrative).map((line, i) => (
                  <li key={`cn-${i}`}>{line}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* ── Allowances & Contingencies ── */}
          <div className="ps">
            <div className="psTitle">Allowances &amp; Contingencies</div>
            <div className="psBody">
              <ul>
                {splitLines(values.allowancesNarrative).length > 0 ? (
                  splitLines(values.allowancesNarrative).map((line, i) => (
                    <li key={i}>{line}</li>
                  ))
                ) : (
                  <li>
                    <strong>Allowances:</strong> None listed
                  </li>
                )}
                {values.gcContingencyAmount && (
                  <li>
                    <strong>Contingency:</strong> {values.gcContingencyAmount}
                    {values.contingencyPct &&
                      ` (${values.contingencyPct} of hard costs)`}
                    {values.industryRange &&
                      ` — industry typical range: ${values.industryRange}`}
                  </li>
                )}
                {splitLines(values.contingencyNarrative).map((line, i) => (
                  <li key={`contn-${i}`}>{line}</li>
                ))}
                {splitLines(values.clarificationsNarrative).map((line, i) => (
                  <li key={`clar-${i}`}>{line}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* ── Budget Analysis ── */}
          <div className="ps">
            <div className="psTitle">
              Construction Budget Analysis — Major Trade Comparison
            </div>
            <div className="psBody">
              <Table
                tableClassName="budgetTable"
                data={values.budgetRows.filter((r) => !r.isTotal)}
                columns={
                  [
                    { header: "Scope", render: (r) => r.trade || "—" },
                    {
                      header: "Scheduled Value",
                      render: (r) => r.value || "—",
                    },
                    { header: "Project SF", render: (r) => r.projectSf || "—" },
                    {
                      header: "Project $/SF",
                      render: (r) => r.projectPsf || "—",
                    },
                    {
                      header: "Industry Low",
                      render: (r) => r.industryLow || "—",
                    },
                    {
                      header: "Industry High",
                      render: (r) => r.industryHigh || "—",
                    },
                  ] as Column<(typeof values.budgetRows)[number]>[]
                }
                footerRow={[
                  <td key="0">Total</td>,
                  <td key="1">{String(totals.value)}</td>,
                  <td key="2">{String(totals.projectSf)}</td>,
                  <td key="3">{totals.projectPsf.toFixed(2)}</td>,
                  <td key="4">{totals.industryLow.toFixed(2)}</td>,
                  <td key="5">{totals.industryHigh.toFixed(2)}</td>,
                ]}
                footerRowClassName="totalRow"
              />
              {values.budgetNarrative && (
                <div className="budgetNarrative">
                  {renderBulletText(values.budgetNarrative)}
                </div>
              )}
              {values.budgetConclusion && (
                <div className="budgetConclusion">
                  {renderBulletText(values.budgetConclusion)}
                </div>
              )}
            </div>
          </div>

          {/* ── Schedule Analysis ── */}
          <div className="ps">
            <div className="psTitle">Schedule Analysis</div>
            <div className="psBody">
              <ul>
                <li>
                  {values.masterScheduleProvided ||
                    "No Master Schedule provided"}
                </li>
                {(values.mobilizationDate || values.estCompletion) && (
                  <li>
                    {values.statedDuration &&
                      `${values.statedDuration} timeframe: `}
                    {values.mobilizationDate &&
                      `mobilization ${values.mobilizationDate}`}
                    {values.mobilizationDate && values.estCompletion && ", "}
                    {values.estCompletion &&
                      `substantial completion ${values.estCompletion}`}
                  </li>
                )}
                {values.typicalDuration && (
                  <li>
                    Projects of this nature typically take{" "}
                    {values.typicalDuration}
                  </li>
                )}
                {splitLines(values.scheduleNarrative).map((line, i) => (
                  <li key={`sn-${i}`}>{line}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* ── Closing Comments ── */}
          <div className="ps">
            <div className="psTitle">Closing Comments / Summary</div>
            <div className="psBody">
              <ul>
                {splitLines(values.closingNarrative).length > 0 ? (
                  splitLines(values.closingNarrative).map((line, i) => (
                    <li key={i}>{line}</li>
                  ))
                ) : (
                  <li>No closing comments entered.</li>
                )}
              </ul>
            </div>
          </div>

          {/* ── Action Items ── */}
          {values.actionItems.some(
            (item) => item.party.trim() || item.action.trim(),
          ) && (
            <div className="ps">
              <div className="psTitle">Action Items</div>
              <div className="psBody">
                <Table
                  tableClassName="actionTable"
                  data={values.actionItems}
                  columns={
                    [
                      {
                        header: "Party",
                        className: "partyCol",
                        render: (r) => r.party,
                      },
                      { header: "Action Item", render: (r) => r.action },
                      {
                        header: "Status",
                        className: "statusCol",
                        render: (r) => (
                          <span style={statusStyle(r.status)}>{r.status}</span>
                        ),
                      },
                    ] as Column<(typeof values.actionItems)[number]>[]
                  }
                />
              </div>
            </div>
          )}

          {/* ── Signature ── */}
          <div className="ps">
            <div className="psSig">
              <div>
                <div className="sigName">
                  {adminName}
                  {adminCompany ? ` · ${adminCompany}` : ""}
                </div>
                <div className="sigMeta">{adminContact}</div>
              </div>
              <div className="sigRight">
                <div className="sigScript">{adminName}</div>
                <div className="sigDate">{values.reviewDate}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Delivery Settings ── */}
      <Card title="Delivery Settings" badge={undefined}>
        <div className="fieldRow c2">
          <div className="field">
            <label>
              Deliver To <span className="req">*</span>
            </label>
            <Input
              className={`autoFill fi${hasErr("deliverTo") ? " error-input-red" : ""}`}
              name="deliverTo"
              value={values.deliverTo}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled
            />
          </div>
          <div className="field overflow-auto" ref={ccFieldRef}>
            <label>CC</label>
            <ChipEmailInput
              value={values.deliveryCC}
              onChange={(val) => setFieldValue("deliveryCC", val)}
              onErrorChange={handleCcErrorChange}
            />
          </div>
        </div>
        <div className="fieldRow c2">
          <div className="field">
            <label>Delivery Method</label>
            <Select
              height={"38px"}
              name="deliveryMethod"
              options={DELIVERY_METHOD_OPTIONS}
              value={values.deliveryMethod}
              onChange={handleChange}
            />
          </div>
          <div className="field">
            <label>Lock After Publishing</label>
            <Select
              height={"38px"}
              name="lockAfterPublish"
              options={LOCK_OPTIONS}
              value={values.lockAfterPublish}
              onChange={handleChange}
            />
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Step8PreviewPublish;
